import type React from 'react';

import { IconCheckRound } from '@/components/apps/ui/icon';

import type {
  SelectAppearance,
  SelectMenuVisualState,
} from './types/SelectAppearance';
import type { SelectOptionDataType } from './types/SelectOptionDataType';

type SelectMenuProps = {
  activeIndex: number;
  appearance: SelectAppearance;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  getOptionId: (index: number) => string;
  isSmallSize: boolean;
  listboxId: string;
  listRef: React.RefObject<HTMLUListElement | null>;
  menuState: SelectMenuVisualState;
  optionRefs: React.RefObject<(HTMLLIElement | null)[]>;
  options: SelectOptionDataType[];
  portalZIndex: number;
  positionMenu: (node: HTMLDivElement | null) => void;
  selectOption: (index: number) => void;
  selectedIndex: number;
  showSelectedIndicator: boolean;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
};

export default function SelectMenu({
  activeIndex,
  appearance,
  ariaLabel,
  ariaLabelledBy,
  getOptionId,
  isSmallSize,
  listboxId,
  listRef,
  menuState,
  optionRefs,
  options,
  portalZIndex,
  positionMenu,
  selectOption,
  selectedIndex,
  showSelectedIndicator,
  setActiveIndex,
}: SelectMenuProps) {
  return (
    // 포커스는 combobox 트리거에 유지되므로 메뉴 래퍼 자체는 상호작용 요소가 아니다
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={positionMenu}
      className={appearance.menuWrapper(menuState)}
      style={{ zIndex: portalZIndex }}
      onMouseDown={(event) => event.preventDefault()}
    >
      <div
        className={appearance.menu(menuState)}
        style={appearance.menuStyle?.(menuState)}
      >
        <ul
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className={appearance.list}
        >
          {options.map((option, index) => (
            // 키보드 입력은 aria-activedescendant를 가진 combobox가 처리한다
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={`${String(option.value)}-${index}`}
              id={getOptionId(index)}
              ref={(node) => {
                optionRefs.current[index] = node;
              }}
              role="option"
              aria-selected={selectedIndex === index}
              aria-disabled={option.isDisabled || undefined}
              onClick={() => selectOption(index)}
              onMouseEnter={() => {
                if (!option.isDisabled) setActiveIndex(index);
              }}
              className={appearance.item(isSmallSize, {
                isActive: selectedIndex === index,
                isDisabled: option.isDisabled,
                isFocused: activeIndex === index,
              })}
            >
              <span className="min-w-0 flex-1">{option.label}</span>
              {showSelectedIndicator && selectedIndex === index ? (
                <IconCheckRound
                  size={16}
                  aria-hidden="true"
                  className="shrink-0"
                />
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
